import { useEffect, useRef, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useScrollIntoViewOn } from "../components/useScrollIntoViewOn";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { parseUtc } from "../utils/datetime";

// Generalized maintenance schedules (one-off / recurring) that suppress ALL
// alerting for the devices they scope to. Backed by /api/v1/maintenance-schedules.

type Schedule = {
  id: number;
  name: string;
  enabled: boolean;
  scope: string;
  customer_id?: number | null;
  schedule_type: string;
  days_of_week: number[];
  start_time?: string | null;
  end_time?: string | null;
  timezone?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  active_now: boolean;
};

type Customer = { id: number; name: string };

const DAYS = [
  { v: 0, l: "Mon" },
  { v: 1, l: "Tue" },
  { v: 2, l: "Wed" },
  { v: 3, l: "Thu" },
  { v: 4, l: "Fri" },
  { v: 5, l: "Sat" },
  { v: 6, l: "Sun" },
];

const TZS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "UTC",
];

const blankForm = {
  name: "Maintenance",
  scope: "global",
  customer_id: "" as number | "",
  schedule_type: "recurring",
  days_of_week: [] as number[],
  start_time: "00:00",
  end_time: "05:00",
  timezone: "America/New_York",
  starts_at: "",
  ends_at: "",
};

function summarize(s: Schedule): string {
  if (s.schedule_type === "recurring") {
    const days = (s.days_of_week || []).map((d) => DAYS.find((x) => x.v === d)?.l).join(", ");
    return `${days || "no days"} ${s.start_time}–${s.end_time} ${s.timezone || ""}`.trim();
  }
  const fmt = (v?: string | null) => (v ? new Date(v).toLocaleString() : "—");
  return `${fmt(s.starts_at)} → ${fmt(s.ends_at)}`;
}

export default function MaintenanceSchedules() {
  const [rows, setRows] = useState<Schedule[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...blankForm });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // Same shape as Reboot Schedules, and the same reported symptom waiting to
  // happen: the form renders above the table, so clicking Edit on a row
  // below the fold opened it out of sight and looked like nothing happened.
  const formRef = useRef<HTMLDivElement>(null);
  useScrollIntoViewOn(formRef, editingId ? `edit-${editingId}` : null);
  const schedSort = useSort("name");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - credential and
  // machine-output fields must not reach a prompt.
  usePublishScreenData(
    "Maintenance windows",
    useMemo(
      () =>
        rows.map((r: any) => ({
          name: r.name,
          customer: r.customer_name ?? null,
          enabled: r.enabled,
          cadence: r.cadence ?? null,
          day_of_week: r.day_of_week ?? null,
          hour: r.hour ?? null,
          timezone: r.timezone ?? null,
        })),
      [rows],
    ),
    undefined,
  );

  const load = () => {
    setLoading(true);
    setErr("");
    api
      .get("/api/v1/maintenance-schedules")
      .then((r) => setRows(r.data || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => {});
  }, []);

  const toggleDay = (d: number) =>
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort((a, b) => a - b),
    }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const body: Record<string, unknown> = {
        name: form.name,
        scope: form.scope,
        schedule_type: form.schedule_type,
      };
      if (form.scope === "customer") body.customer_id = form.customer_id || null;
      if (form.schedule_type === "recurring") {
        body.days_of_week = form.days_of_week;
        body.start_time = form.start_time;
        body.end_time = form.end_time;
        body.timezone = form.timezone;
      } else {
        body.starts_at = form.starts_at ? new Date(form.starts_at).toISOString() : null;
        body.ends_at = form.ends_at ? new Date(form.ends_at).toISOString() : null;
      }
      if (editingId) {
        await api.put(`/api/v1/maintenance-schedules/${editingId}`, body);
      } else {
        await api.post("/api/v1/maintenance-schedules", body);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...blankForm });
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ISO (UTC) → the value a <input type=datetime-local> expects (local wall clock).
  const toLocalInput = (iso?: string | null) => {
    if (!iso) return "";
    // parseUtc, not new Date: the API sends zone-less UTC, so new Date
    // read it as local and the offset below was then applied twice.
    const d = parseUtc(iso);
    if (!d) return "";
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const startEdit = (s: Schedule) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      scope: s.scope,
      customer_id: s.customer_id ?? "",
      schedule_type: s.schedule_type,
      days_of_week: s.days_of_week || [],
      start_time: s.start_time || "00:00",
      end_time: s.end_time || "05:00",
      timezone: s.timezone || "America/New_York",
      starts_at: toLocalInput(s.starts_at),
      ends_at: toLocalInput(s.ends_at),
    });
    setShowForm(true);
  };

  const remove = async (id: number) => {
    if (!window.confirm("Delete this maintenance schedule?")) return;
    try {
      await api.delete(`/api/v1/maintenance-schedules/${id}`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const custName = (id?: number | null) =>
    id ? customers.find((c) => c.id === id)?.name || `#${id}` : "—";

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Maintenance Schedules
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Suppress all alerting for a window — one-off or recurring (e.g. weeknights 00:00–05:00),
            across all devices or a single customer.
          </p>
        </div>
        <button
          onClick={() => {
            setForm({ ...blankForm });
            setEditingId(null);
            setShowForm((v) => !v);
          }}
          className="px-3 py-1.5 rounded-lg text-sm bg-brand text-white shrink-0"
        >
          {showForm ? "Cancel" : "New schedule"}
        </button>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      {showForm && (
        <div
          ref={formRef}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 space-y-3"
        >
          {editingId !== null && (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Editing{" "}
              <span className="text-brand dark:text-brand-light">
                {rows.find((s) => s.id === editingId)?.name || `#${editingId}`}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Name</span>
              <input
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Applies to</span>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
              >
                <option value="global">All devices</option>
                <option value="customer">A customer</option>
              </select>
            </label>
            {form.scope === "customer" && (
              <label className="text-sm">
                <span className="block text-gray-600 dark:text-gray-300 mb-1">Customer</span>
                <select
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                  value={form.customer_id}
                  onChange={(e) =>
                    setForm({ ...form, customer_id: e.target.value ? Number(e.target.value) : "" })
                  }
                >
                  <option value="">Select…</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-sm">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Type</span>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.schedule_type}
                onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}
              >
                <option value="recurring">Recurring (weekly)</option>
                <option value="one_off">One-off (date range)</option>
              </select>
            </label>
          </div>

          {form.schedule_type === "recurring" ? (
            <div className="space-y-3">
              <div>
                <span className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Days</span>
                <div className="flex flex-wrap gap-1.5">
                  {DAYS.map((d) => (
                    <button
                      key={d.v}
                      type="button"
                      onClick={() => toggleDay(d.v)}
                      className={`px-2.5 py-1 rounded-lg text-sm border ${
                        form.days_of_week.includes(d.v)
                          ? "bg-brand text-white border-brand"
                          : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="text-sm">
                  <span className="block text-gray-600 dark:text-gray-300 mb-1">Start</span>
                  <input
                    type="time"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-600 dark:text-gray-300 mb-1">End</span>
                  <input
                    type="time"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-600 dark:text-gray-300 mb-1">Timezone</span>
                  <select
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                    value={form.timezone}
                    onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  >
                    {TZS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="text-xs text-gray-400">
                End before start crosses midnight (e.g. 22:00–04:00).
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-gray-600 dark:text-gray-300 mb-1">Start</span>
                <input
                  type="datetime-local"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                />
              </label>
              <label className="text-sm">
                <span className="block text-gray-600 dark:text-gray-300 mb-1">End</span>
                <input
                  type="datetime-local"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                />
              </label>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-1.5 rounded-lg text-sm bg-brand text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : editingId ? "Update schedule" : "Create schedule"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-6 text-sm text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
            No maintenance schedules yet.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500">
              <tr>
                <SortableTh label="Name" sortKey="name" sort={schedSort.sort} onSort={schedSort.toggle} />
                <th className="px-3 py-2 text-left">Applies to</th>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(rows, schedSort.sort, (s) => (s.name || "").toLowerCase()).map((s) => (
                <tr key={s.id}>
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2">
                    {s.scope === "global"
                      ? "All devices"
                      : s.scope === "customer"
                        ? custName(s.customer_id)
                        : s.scope}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{summarize(s)}</td>
                  <td className="px-3 py-2">
                    {!s.enabled ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                        disabled
                      </span>
                    ) : s.active_now ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                        active now
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                        scheduled
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => startEdit(s)}
                      className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 mr-1.5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(s.id)}
                      className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
